import * as React from "react"
import {
  SettingsBlock,
  SettingsBlockContent,
  SettingsCardSection,
} from "@modules/site/settings"
import Loading from "@modules/ui/components/Loading"
import { CmsIntegrationRow } from "./CmsIntegrationRow"
import { useCmsIntegrationControls } from "../hooks/useCmsIntegrationControls"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { CmsProvider } from "@modules/graphql/types"

type CmsIntegrationContentProps = {
  loading: boolean
  error: { message: string }
  cmsIntegrations: CmsProvider[]
  siteId: string
  orgSiteName: string
  permissionToEdit: boolean
}

function CmsIntegrationContent({
  loading,
  error,
  cmsIntegrations,
  siteId,
  orgSiteName,
  permissionToEdit,
}: CmsIntegrationContentProps) {
  const {
    setCmsToEdit,
    setCmsToRemove,
    controlModals,
  } = useCmsIntegrationControls(siteId, orgSiteName)

  if (loading) {
    return (
      <Loading
        delay={1000}
        message="loading available cms integrations..."
        bufferSize="none"
      />
    )
  }

  if (error) {
    return (
      <SettingsCardSection applyPaddingTop={false}>
        <ErrorAlert>{error.message}</ErrorAlert>
      </SettingsCardSection>
    )
  }

  if (cmsIntegrations.length === 0) {
    return <p>No integrations</p>
  }

  return (
    <React.Fragment>
      {cmsIntegrations.map(cmsIntegration => {
        return (
          <CmsIntegrationRow
            key={cmsIntegration.name}
            cms={cmsIntegration}
            editable={permissionToEdit}
            onEdit={() => setCmsToEdit(cmsIntegration)}
            onDelete={() => setCmsToRemove(cmsIntegration)}
          />
        )
      })}

      {controlModals}
    </React.Fragment>
  )
}

type CmsIntegrationCardProps = {
  loading: boolean
  error: { message: string }
  cmsIntegrations: CmsProvider[]
  title: string
  docUrl: string
  siteId: string
  orgSiteName: string
  permissionToEdit: boolean
  integrationsBlockId: string
  children: React.ReactNode
}

export function CmsIntegrationCard({
  loading,
  error,
  cmsIntegrations = [],
  title,
  docUrl,
  siteId,
  orgSiteName,
  permissionToEdit = true,
  integrationsBlockId,
  children,
}: CmsIntegrationCardProps) {
  return (
    <SettingsBlock
      data-cy={`settings-block-${title.replace(/\s/g, ``).toLowerCase()}`}
      title={title}
      docUrl={docUrl}
      id={integrationsBlockId}
    >
      <SettingsBlockContent>
        <CmsIntegrationContent
          loading={loading}
          error={error}
          cmsIntegrations={cmsIntegrations}
          siteId={siteId}
          orgSiteName={orgSiteName}
          permissionToEdit={permissionToEdit}
        />
        {children}
      </SettingsBlockContent>
    </SettingsBlock>
  )
}
